import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { useSnackbar } from "../../context/SnackbarContext";
import { handleApi } from "../../apis";
import { GET_BUSINESS_REPORTS } from "../../apis/configuration";
import { useNavigate } from "react-router-dom";

export const useReports = (I18n = {}) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [salesSummary, setSalesSummary] = useState([
    { label: I18n.total_sales, value: null },
    { label: I18n.cost_of_stock_sold, value: null },
    { label: I18n.gross_profit, value: null },
    { label: I18n.expenses, value: null },
    { label: I18n.net_profit, value: null },
  ]);
  const [salesByPaymentMethod, setSalesByPaymentMethod] = useState([]);
  const [salesByPaymentMethodTotal, setSalesByPaymentMethodTotal] = useState(0);
  const [salesByChannel, setSalesByChannel] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [topSellingItemsTotal, setTopSellingItemsTotal] = useState(0);
  const [lowSellingItems, setLowSellingItems] = useState([]);
  const [lowSellingItemsTotal, setLowSellingItemsTotal] = useState(0);
  const [profitMargins, setProfitMargins] = useState([]);
  const [profitMarginsTotal, setProfitMarginsTotal] = useState(0);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [salesByCategoryTotal, setSalesByCategoryTotal] = useState(0);
  const [expenseByCategory, setExpenseByCategory] = useState([]);
  const [expenseByCategoryTotal, setExpenseByCategoryTotal] = useState(0);
  const [topContactsBySale, setTopContactsBySale] = useState([]);
  const [topContactsBySaleTotal, setTopContactsBySaleTotal] = useState(0);
  const [discountSummary, setDiscountSummary] = useState({
    total_discount: null,
    orders_with_discounts: null,
    average_discount_per_order: null,
  });
  const [taxSummary, setTaxSummary] = useState({
    total_tax_collected: null,
    taxable_sales: null,
    tax_free_sales: null,
    multiple_tax_rates: [],
  });

  const cancelTokenRef = useRef(null);

  const resetReportState = useCallback((report_type) => {
    switch (report_type) {
      case "sale_summary":
        setSalesSummary([
          { label: I18n.total_sales, value: null },
          { label: I18n.cost_of_stock_sold, value: null },
          { label: I18n.gross_profit, value: null },
          { label: I18n.expenses, value: null },
          { label: I18n.net_profit, value: null },
        ]);
        break;
      case "sales_by_payment_method":
        setSalesByPaymentMethod([]);
        setSalesByPaymentMethodTotal(0);
        break;
      case "sales_by_channel":
        setSalesByChannel([]);
        break;
      case "top_selling_items":
        setTopSellingItems([]);
        setTopSellingItemsTotal(0);
        break;
      case "low_selling_items":
        setLowSellingItems([]);
        setLowSellingItemsTotal(0);
        break;
      case "profit_margins":
        setProfitMargins([]);
        setProfitMarginsTotal(0);
        break;
      case "sales_by_category":
        setSalesByCategory([]);
        setSalesByCategoryTotal(0);
        break;
      case "expense_by_category":
        setExpenseByCategory([]);
        setExpenseByCategoryTotal(0);
        break;
      case "top_contacts_by_sale":
        setTopContactsBySale([]);
        setTopContactsBySaleTotal(0);
        break;
      case "discount_summary":
        setDiscountSummary({ total_discount: null, orders_with_discounts: null, average_discount_per_order: null });
        break;
      case "tax_summary":
        setTaxSummary({ total_tax_collected: null, taxable_sales: null, tax_free_sales: null, multiple_tax_rates: [] });
        break;
      default:
        break;
    }
  }, [I18n]);

  const handleFetchReport = useCallback(async ({
    business_id,
    report_type,
    start_date,
    end_date,
    limit,
    offset,
  }) => {
    const isPagination =
      (["top_selling_items", "low_selling_items", "profit_margins", "sales_by_category", "expense_by_category", "top_contacts_by_sale"].includes(report_type)) && offset > 0;

    cancelTokenRef.current?.cancel();
    cancelTokenRef.current = axios.CancelToken.source();

    if (!isPagination) resetReportState(report_type);

    try {
      if (isPagination) {
        setPageLoading(true);
      } else {
        setLoading(true);
      }

      const payload = {
        business_id,
        report_type,
        start_date,
        end_date,
        ...(limit !== undefined && { limit }),
        ...(offset !== undefined && { offset }),
      };

      const response = await handleApi(
        GET_BUSINESS_REPORTS,
        payload,
        cancelTokenRef.current,
        navigate,
      );

      if (response?.success) {
        if (response?.report_type === "sale_summary") {
          const { data } = response;
          setSalesSummary([
            { label: I18n.total_sales, value: data?.total_sales },
            { label: I18n.cost_of_stock_sold, value: data?.cost_of_stock_sold },
            { label: data?.gross_profit < 0 ? I18n.gross_loss : I18n.gross_profit, value: data?.gross_profit },
            { label: I18n.expenses, value: data?.expenses },
            { label: data?.net_profit < 0 ? I18n.net_loss : I18n.net_profit, value: data?.net_profit },
          ]);
        } else if (response?.report_type === "sales_by_channel") {
          const rows = response?.data || [];
          setSalesByChannel(rows);
        } else if (response?.report_type === "top_selling_items") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setTopSellingItemsTotal(total);
          if (offset > 0) {
            setTopSellingItems((prev) => [...prev, ...rows]);
          } else {
            setTopSellingItems(rows);
          }
        } else if (response?.report_type === "low_selling_items") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setLowSellingItemsTotal(total);
          if (offset > 0) {
            setLowSellingItems((prev) => [...prev, ...rows]);
          } else {
            setLowSellingItems(rows);
          }
        } else if (response?.report_type === "profit_margins") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setProfitMarginsTotal(total);
          if (offset > 0) {
            setProfitMargins((prev) => [...prev, ...rows]);
          } else {
            setProfitMargins(rows);
          }
        } else if (response?.report_type === "sales_by_category") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setSalesByCategoryTotal(total);
          if (offset > 0) {
            setSalesByCategory((prev) => [...prev, ...rows]);
          } else {
            setSalesByCategory(rows);
          }
        } else if (response?.report_type === "expense_by_category") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setExpenseByCategoryTotal(total);
          if (offset > 0) {
            setExpenseByCategory((prev) => [...prev, ...rows]);
          } else {
            setExpenseByCategory(rows);
          }
        } else if (response?.report_type === "top_contacts_by_sale") {
          const rows = response?.data || [];
          const total = response?.total ?? rows.length;
          setTopContactsBySaleTotal(total);
          if (offset > 0) {
            setTopContactsBySale((prev) => [...prev, ...rows]);
          } else {
            setTopContactsBySale(rows);
          }
        } else if (response?.report_type === "discount_summary") {
          const { data } = response;
          setDiscountSummary({
            total_discount: data?.total_discount,
            orders_with_discounts: data?.orders_with_discounts,
            average_discount_per_order: data?.average_discount_per_order,
          });
        } else if (response?.report_type === "tax_summary") {
          const { data } = response;
          setTaxSummary({
            total_tax_collected: data?.total_tax_collected,
            taxable_sales: data?.taxable_sales,
            tax_free_sales: data?.tax_free_sales,
            multiple_tax_rates: data?.multiple_tax_rates || [],
          });
        } else if (response?.report_type === "sales_by_payment_method") {
          const rows = response?.data?.rows || [];
          // Light hex is the var fallback; dark values live in SalesByPaymentMethod.module.css
          const METHOD_COLORS = {
            Cash: "var(--reportBarCash, #CBD5E1)",
            Bank: "var(--reportBarBank, #E2E8F0)",
          };
          const FALLBACK_COLORS = [
            "var(--reportBarFallback0, #94A3B8)",
            "var(--reportBarFallback1, #64748B)",
            "var(--reportBarFallback2, #475569)",
            "var(--reportBarFallback3, #334155)",
            "var(--reportBarFallback4, #1E293B)",
            "var(--reportBarFallback5, #F1F5F9)",
          ];
          let fallbackIdx = 0;
          setSalesByPaymentMethodTotal(response?.data?.total_collected ?? 0);
          setSalesByPaymentMethod(
            rows.map((r) => ({
              method: r.payment_method,
              transactions: r.transactions,
              total_sales: r.total_sales,
              percentage: r.percentage ?? 0,
              barColor:
                METHOD_COLORS[r.payment_method] ??
                FALLBACK_COLORS[fallbackIdx++ % FALLBACK_COLORS.length],
            })),
          );
        }
      } else {
        showSnackbar({
          message: response?.message || I18n.something_went_wrong,
          severity: "error",
        });
      }
      if (isPagination) setPageLoading(false);
      else setLoading(false);
    } catch (err) {
      if (axios.isCancel(err)) {
        if (isPagination) setPageLoading(false);
        return;
      }
      showSnackbar({
        message: err?.response?.data?.message || I18n.something_went_wrong,
        severity: "error",
      });
      if (isPagination) setPageLoading(false);
      else setLoading(false);
    }
  }, [showSnackbar, navigate, resetReportState, I18n]);

  return {
    loading,
    pageLoading,
    salesSummary,
    salesByPaymentMethod,
    salesByPaymentMethodTotal,
    salesByChannel,
    topSellingItems,
    topSellingItemsTotal,
    lowSellingItems,
    lowSellingItemsTotal,
    profitMargins,
    profitMarginsTotal,
    salesByCategory,
    salesByCategoryTotal,
    expenseByCategory,
    expenseByCategoryTotal,
    topContactsBySale,
    topContactsBySaleTotal,
    discountSummary,
    taxSummary,
    handleFetchReport,
  };
};
